import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  Target,
  TrendingUp,
  MapPin,
  MessageSquare,
  CheckCircle,
  QrCode,
  Layers,
  Database,
  ShieldCheck,
  CreditCard,
  BellRing,
} from "lucide-react";

const MODULES = [
  {
    id: "pre-sales",
    title: "Pre-Sales",
    subtitle: "Lead Gen & Marketing Operations",
    icon: Target,
    themeColor: "from-pink-500/20 to-pink-600/5",
    glowColor: "group-hover:from-pink-500/10",
    iconBg: "bg-pink-500/10 border-pink-500/20",
    iconColor: "text-pink-400",
    points: [
      {
        title: "Omnichannel Lead Capture",
        desc: "Pull leads instantly from webhooks, WhatsApp, 99acres, Magicbricks, and Housing.com.",
        detailIcon: Database,
      },
      {
        title: "QR Campaign Micro-Sites",
        desc: "Generate QR codes and project subdomains (e.g. project.clozdl.ai) with built-in enquiry forms.",
        detailIcon: QrCode,
      },
      {
        title: "Smart Routing & Distribution",
        desc: "Assign incoming leads instantly using custom criteria-based or round-robin routing rules.",
        detailIcon: Layers,
      },
      {
        title: "Channel Partner Network",
        desc: "Sourcing managers can onboard broker lists, share property listings, and track CP performance.",
        detailIcon: Users,
      },
    ],
  },
  {
    id: "sales",
    title: "Sales",
    subtitle: "Conversion & Deal Velocity",
    icon: TrendingUp,
    themeColor: "from-purple-500/20 to-purple-600/5",
    glowColor: "group-hover:from-purple-500/10",
    iconBg: "bg-purple-500/10 border-purple-500/20",
    iconColor: "text-purple-400",
    points: [
      {
        title: "Interactive Deal Pipelines",
        desc: "Drag and drop leads through custom Kanban stages mapped directly to your sales workflow.",
        detailIcon: Layers,
      },
      {
        title: "Personalized Message Templates",
        desc: "Send follow-ups using variable-driven WhatsApp, SMS, and Email templates with live previews.",
        detailIcon: MessageSquare,
      },
      {
        title: "Follow-up Tasks & Reminders",
        desc: "Snooze tasks, log calls, and set high-priority callback reminders so no deal leaks.",
        detailIcon: BellRing,
      },
      {
        title: "Real-Time Unit Inventory Matrix",
        desc: "Track tower and unit availability (available, blocked, booked) with complete audit logging.",
        detailIcon: Database,
      },
    ],
  },
  {
    id: "post-sales",
    title: "Post-Sales",
    subtitle: "Operations & Payments Collections",
    icon: ShieldCheck,
    themeColor: "from-[#4B73FF]/20 to-[#4B73FF]/5",
    glowColor: "group-hover:from-[#4B73FF]/10",
    iconBg: "bg-[#4B73FF]/10 border-[#4B73FF]/20",
    iconColor: "text-[#4B73FF]",
    points: [
      {
        title: "Digital Booking Flow",
        desc: "Verify customer emails, compile booking document checklists, and upload verification files.",
        detailIcon: ShieldCheck,
      },
      {
        title: "Construction-Linked Payments",
        desc: "Attach customized payment milestone schedules to units or apply them in bulk to entire towers.",
        detailIcon: CreditCard,
      },
      {
        title: "Collections & Dues Engine",
        desc: "Analyze aging reports, issue demand letters, send payment reminders, and print PDF receipts.",
        detailIcon: CreditCard,
      },
      {
        title: "Geofenced Site Operations",
        desc: "Configure coordinates for sales teams to securely record checked-in site visit attendance.",
        detailIcon: MapPin,
      },
    ],
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const featureVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, delay: 0.1 * i },
  }),
};

export function ModulesSection() {
  return (
    <section className="relative z-10 py-20 sm:py-24 lg:py-28" aria-label="Clozdl.ai modules">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <p className="text-xs tracking-widest uppercase text-[#4B73FF] font-semibold mb-3">
            Core Modules
          </p>
          <h2 className="text-[2.1rem] font-bold leading-tight text-white tracking-tight sm:text-[2.6rem] lg:text-[3rem] mb-4">
            The Complete Lifecycle{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-white to-[#4B73FF]">
              Operating System
            </span>
          </h2>
          <p className="text-base text-white/50 leading-relaxed font-normal">
            Clozdl.ai replaces fragmented tools, spreadsheets, and manual steps with an integrated
            workspace that connects your marketing, sales, and accounts.
          </p>
        </motion.div>

        {/* Modules Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch"
        >
          {MODULES.map((mod) => {
            const ModIcon = mod.icon;
            return (
              <motion.div
                id={mod.id}
                key={mod.id}
                variants={cardVariants}
                className="group flex flex-col relative rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-8 transition-all duration-300 hover:border-white/20 hover:-translate-y-2 shadow-[0_10px_30px_rgba(0,0,0,0.4)] overflow-hidden"
              >
                {/* Glowing hover effect */}
                <div
                  className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${mod.glowColor} to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                />

                {/* Top accent line on hover */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-gradient-to-r group-hover:from-[#4B73FF] group-hover:to-[#0017ff] transition-all duration-300 rounded-t-2xl" />

                {/* Header */}
                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-xl border ${mod.iconBg}`}
                  >
                    <ModIcon className={`h-6 w-6 ${mod.iconColor}`} />
                  </span>
                  <div>
                    <h3 className="text-xl font-bold text-white">{mod.title}</h3>
                    <p className="text-xs text-white/40 font-medium">{mod.subtitle}</p>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-white/5 w-full mb-6 relative z-10" />

                {/* Feature list */}
                <div className="flex-1 flex flex-col gap-6 relative z-10">
                  {mod.points.map((pt, index) => (
                    <motion.div
                      key={index}
                      custom={index}
                      variants={featureVariants}
                      className="flex items-start gap-3"
                    >
                      <span className="flex h-6 w-6 mt-0.5 shrink-0 items-center justify-center rounded-full bg-white/5 text-white/40">
                        <CheckCircle className={`h-4 w-4 ${mod.iconColor}`} />
                      </span>
                      <div>
                        <h4 className="text-sm font-semibold text-white/90 leading-snug">
                          {pt.title}
                        </h4>
                        <p className="text-xs text-white/50 leading-relaxed mt-1">{pt.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
